import { toast } from '@/hooks/use-toast';
import { Product } from '@/types';

export interface PendingAction {
  id: string;
  type: 'wishlist_add' | 'wishlist_remove' | 'cart_add' | 'order_create' | 'custom';
  payload: any;
  description?: string;
  customHandler?: () => Promise<void> | void;
  executed?: boolean;
}

class PendingActionsManager {
  private static instance: PendingActionsManager;
  private pendingActions: Map<string, PendingAction> = new Map();
  private listeners: Set<(actions: PendingAction[]) => void> = new Set();

  private constructor() {}

  static getInstance(): PendingActionsManager {
    if (!PendingActionsManager.instance) {
      PendingActionsManager.instance = new PendingActionsManager();
    }
    return PendingActionsManager.instance;
  }

  addAction(action: Omit<PendingAction, 'id'>): string {
    const id = Math.random().toString(36).substr(2, 9);
    const fullAction: PendingAction = { ...action, id };
    
    this.pendingActions.set(id, fullAction);
    this.notifyListeners();
    
    return id;
  }

  removeAction(id: string): void {
    this.pendingActions.delete(id);
    this.notifyListeners();
  }

  getActions(): PendingAction[] {
    return Array.from(this.pendingActions.values());
  }

  clearActions(): void {
    this.pendingActions.clear();
    this.notifyListeners();
  }

  async executeActions(): Promise<void> {
    const actions = this.getActions().filter(action => !action.executed);
    
    if (actions.length === 0) {
      console.log('No pending actions to execute or all actions already executed');
      return;
    }

    console.log(`Executing ${actions.length} pending actions`);

    for (const action of actions) {
      try {
        // Mark as executed before processing to prevent double execution
        this.markActionAsExecuted(action.id);
        
        await this.executeAction(action);
        this.removeAction(action.id);
        
        console.log(`Successfully executed action ${action.id}: ${action.type}`);
      } catch (error) {
        console.error(`Failed to execute pending action ${action.id}:`, error);
        this.removeAction(action.id);
        
        toast({
          title: "Action failed",
          description: action.description || "A pending action failed to execute",
          variant: "destructive",
        });
      }
    }
  }

  private markActionAsExecuted(id: string): void {
    const action = this.pendingActions.get(id);
    if (action) {
      action.executed = true;
      this.pendingActions.set(id, action);
    }
  }

  private async executeAction(action: PendingAction): Promise<void> {
    switch (action.type) {
      case 'custom':
        if (action.customHandler) {
          await action.customHandler();
        }
        break;
        
      case 'wishlist_add':
        await this.executeWishlistAdd(action.payload);
        break;
        
      case 'wishlist_remove':
        await this.executeWishlistRemove(action.payload);
        break;
        
      case 'cart_add':
        await this.executeCartAdd(action.payload);
        break;
        
      case 'order_create':
        await this.executeOrderCreate(action.payload);
        break;
        
      default:
        console.warn(`Unknown action type: ${action.type}`);
    }
  }

  private async executeWishlistAdd(payload: { product: Product }): Promise<void> {
    // Import dynamically to avoid circular dependencies
    const { useWishlist } = await import('@/contexts/WishlistContext');
    // This will be handled by the WishlistContext's internal addToWishlist method
    // We'll create a separate internal method that doesn't check auth
    console.log('Executing wishlist add for:', payload.product.name);
  }

  private async executeWishlistRemove(payload: { productId: string }): Promise<void> {
    console.log('Executing wishlist remove for:', payload.productId);
  }

  private async executeCartAdd(payload: { product: Product; quantity: number }): Promise<void> {
    console.log('Executing cart add for:', payload.product.name);
  }

  private async executeOrderCreate(payload: any): Promise<void> {
    console.log('Executing order creation');
  }

  subscribe(listener: (actions: PendingAction[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const actions = this.getActions();
    this.listeners.forEach(listener => listener(actions));
  }
}

export const pendingActionsManager = PendingActionsManager.getInstance();